import { useQuery } from '@tanstack/react-query'
import { Button, CollapsibleSection, SpinnerWithBackground, Text } from '@viapeople/components'
import { useState } from 'react'
import { formatStepName } from '../../../utils/helpers'
import { trpc } from '../../../utils/trpc'

interface RawResponsesData {
	context: {
		responses: {
			byRole: Record<
				string,
				Array<{
					question: string
					response: string
					evaluator: string
					questionId: string
				}>
			>
			flat: Array<{
				role: string
				question: string
				response: string
				evaluator: string
				questionId: string
			}>
		}
		minCharThreshold: number
		minCommentThreshold: number
	}
}

export interface AiSummary {
	id: string | null
	configId?: string
	label?: string
	summaryText: string | null
	generatedAt: Date | string | null
	pendingRegeneration?: boolean
	config?: {
		id: string
		label: string
		triggerWorkflowStep?: string
		displayAfterComponentId?: string | null
	}
}

interface AiSummaryListProps {
	aiSummaries: AiSummary[]
	evaluationId: string
	onRefresh?: () => void
}

interface RawResponsesSectionProps {
	configId: string
	evaluationId: string
	isExpanded: boolean
	onToggle: () => void
	viewMode: 'byRole' | 'flat'
	setViewMode: (mode: 'byRole' | 'flat') => void
	expandedRoles: Record<string, boolean>
	onToggleRole: (role: string, next: boolean) => void
	expandAll: (rawResponses: RawResponsesData | null) => void
	collapseAll: () => void
}

const RawResponsesSection = ({
	configId,
	evaluationId,
	isExpanded,
	onToggle,
	viewMode,
	setViewMode,
	expandedRoles,
	onToggleRole,
	expandAll,
	collapseAll,
}: RawResponsesSectionProps) => {
	// Get raw responses for context - only when showing raw responses
	const {
		data: rawResponses,
		isLoading: isResponsesLoading,
		error: responsesError,
	} = useQuery({
		...trpc.client.aiSummaries.context.show.queryOptions({ evaluationId, configId }),
		enabled: !!configId && !!evaluationId && isExpanded,
	})

	return (
		<CollapsibleSection
			title='View Source Responses'
			defaultOpen={isExpanded}
			isOpen={isExpanded}
			onToggle={() => onToggle()}>
			{isExpanded && (
				<div className='mt-4 space-y-4'>
					{/* View Mode Toggle */}
					<div className='flex items-center justify-between'>
						<div className='flex gap-2'>
							<Button
								size='small'
								variant={viewMode === 'byRole' ? 'primary' : 'secondary'}
								onClick={() => setViewMode('byRole')}>
								By Role
							</Button>
							<Button
								size='small'
								variant={viewMode === 'flat' ? 'primary' : 'secondary'}
								onClick={() => setViewMode('flat')}>
								All Responses
							</Button>
						</div>

						{viewMode === 'byRole' && (
							<div className='flex gap-2'>
								<Button size='small' variant='tertiary' onClick={() => expandAll(rawResponses ?? null)}>
									Expand All
								</Button>
								<Button size='small' variant='tertiary' onClick={collapseAll}>
									Collapse All
								</Button>
							</div>
						)}
					</div>

					{/* Raw Responses Content */}
					{isResponsesLoading ? (
						<div className='py-4'>
							<SpinnerWithBackground backgroundClass='opacity-0' />
						</div>
					) : responsesError ? (
						<div className='py-4 text-center text-red-600'>
							<Text variant='small'>Error loading responses: {responsesError.message}</Text>
						</div>
					) : rawResponses?.context?.responses ? (
						<div className='space-y-4'>
							{viewMode === 'byRole' ? (
								// By Role View
								Object.entries(rawResponses.context.responses.byRole).map(([role, responses]) => (
									<CollapsibleSection
										key={role}
										title={`${formatStepName(role)} (${responses.length} responses)`}
										defaultOpen={expandedRoles[role] || false}
										isOpen={expandedRoles[role] || false}
										onToggle={(next) => onToggleRole(role, next)}>
										<div className='mt-3 space-y-3'>
											{responses.map((response) => (
												<div key={`${response.evaluator}-${response.question}`} className='bg-neutral-50 rounded p-3'>
													<div className='flex justify-between items-start mb-2'>
														<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
															{response.question}
														</Text>
														<Text variant='xsmall' className='text-neutral-500'>
															{response.evaluator}
														</Text>
													</div>
													<Text variant='small'>{response.response}</Text>
												</div>
											))}
										</div>
									</CollapsibleSection>
								))
							) : (
								// Flat View
								<div className='space-y-3'>
									{rawResponses.context.responses.flat.map((response) => (
										<div
											key={`${response.role}-${response.evaluator}-${response.question}`}
											className='bg-neutral-50 rounded p-3'>
											<div className='flex justify-between items-start mb-2'>
												<div className='flex gap-2'>
													<span className='inline-flex items-center px-2 py-1 rounded-sm text-xs font-medium bg-blue-100 text-blue-800'>
														{formatStepName(response.role)}
													</span>
													<Text variant='xsmall' weight='semibold' className='text-neutral-700'>
														{response.question}
													</Text>
												</div>
												<Text variant='xsmall' className='text-neutral-500'>
													{response.evaluator}
												</Text>
											</div>
											<Text variant='small'>{response.response}</Text>
										</div>
									))}
								</div>
							)}
						</div>
					) : rawResponses?.context ? (
						<div className='py-4 text-center'>
							<Text variant='small'>No responses meet the minimum criteria for this summary.</Text>
							<Text variant='xsmall' className='text-neutral-500 mt-1'>
								(Min {rawResponses.context.minCharThreshold} characters, {rawResponses.context.minCommentThreshold}{' '}
								comments required)
							</Text>
						</div>
					) : (
						<div className='py-4 text-center'>
							<Text variant='small'>No source responses available.</Text>
						</div>
					)}
				</div>
			)}
		</CollapsibleSection>
	)
}

// Helper function to extract summary metadata
const getSummaryId = (summary: AiSummary, index: number): string => {
	return summary.config?.id || summary.id || summary.configId || `summary-${index}`
}

const getSummaryLabel = (summary: AiSummary): string => {
	return summary.config?.label || summary.label || 'AI Summary'
}

const getSummaryConfigId = (summary: AiSummary): string => {
	return summary.config?.id || summary.configId || ''
}

interface AiSummaryCardProps {
	summary: AiSummary
	evaluationId: string
	expandedSummaries: Record<string, boolean>
	setExpandedSummaries: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
	viewMode: 'byRole' | 'flat'
	setViewMode: (mode: 'byRole' | 'flat') => void
	expandedRoles: Record<string, boolean>
	setExpandedRoles: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
	expandAll: (rawResponses: RawResponsesData | null) => void
	collapseAll: () => void
}

const AiSummaryCard = ({
	summary,
	evaluationId,
	expandedSummaries,
	setExpandedSummaries,
	viewMode,
	setViewMode,
	expandedRoles,
	setExpandedRoles,
	expandAll,
	collapseAll,
}: AiSummaryCardProps) => {
	const summaryLabel = getSummaryLabel(summary)
	const configId = getSummaryConfigId(summary)
	const hasConfigId = summary.id && (summary.config?.id || summary.configId)

	return (
		<div className='flex flex-col gap-4 p-6 bg-white rounded-lg border border-neutral-200'>
			<div className='flex items-center justify-between'>
				<Text variant='medium' weight='semibold'>
					{summaryLabel}
				</Text>
				{summary.id && summary.generatedAt && (
					<Text variant='xsmall' className='text-neutral-500'>
						Generated {summary.generatedAt ? new Date(summary.generatedAt).toLocaleString() : '-'}
					</Text>
				)}
			</div>

			{/* AI Summary Content */}
			{summary.summaryText ? (
				<div className='bg-blue-50 rounded-lg p-4'>
					<Text variant='small' className='whitespace-pre-wrap'>
						{summary.summaryText}
					</Text>
				</div>
			) : (
				<div className='py-8 text-center'>
					<Text variant='small'>AI summary will be available once the required responses are collected.</Text>
				</div>
			)}

			{/* Raw Responses Section */}
			{hasConfigId && (
				<RawResponsesSection
					configId={configId}
					evaluationId={evaluationId}
					isExpanded={expandedSummaries[configId] || false}
					onToggle={() => {
						setExpandedSummaries((prev) => ({
							...prev,
							[configId]: !prev[configId],
						}))
					}}
					viewMode={viewMode}
					setViewMode={setViewMode}
					expandedRoles={expandedRoles}
					onToggleRole={(role, next) =>
						setExpandedRoles((prev) => ({
							...prev,
							[role]: next,
						}))
					}
					expandAll={expandAll}
					collapseAll={collapseAll}
				/>
			)}
		</div>
	)
}

export const AiSummaryList = ({ aiSummaries, evaluationId }: AiSummaryListProps) => {
	const [expandedSummaries, setExpandedSummaries] = useState<Record<string, boolean>>({})
	const [viewMode, setViewMode] = useState<'byRole' | 'flat'>('byRole')
	const [expandedRoles, setExpandedRoles] = useState<Record<string, boolean>>({})

	const expandAll = (rawResponses: RawResponsesData | null) => {
		const allRoles = Object.keys(rawResponses?.context?.responses?.byRole || {})
		const expanded = Object.fromEntries(allRoles.map((role) => [role, true]))
		setExpandedRoles(expanded)
	}

	const collapseAll = () => {
		setExpandedRoles({})
	}

	if (!aiSummaries.length) {
		return null
	}

	return (
		<div className='space-y-4'>
			{aiSummaries.map((summary, index) => (
				<AiSummaryCard
					key={getSummaryId(summary, index)}
					summary={summary}
					evaluationId={evaluationId}
					expandedSummaries={expandedSummaries}
					setExpandedSummaries={setExpandedSummaries}
					viewMode={viewMode}
					setViewMode={setViewMode}
					expandedRoles={expandedRoles}
					setExpandedRoles={setExpandedRoles}
					expandAll={expandAll}
					collapseAll={collapseAll}
				/>
			))}
		</div>
	)
}
